import { useMutation } from "@apollo/client";
import type {
  DeleteConnectionInput,
  DeleteConnectionPayload,
  Connection,
} from "@casey/db-types";
import {
  DELETE_INTERVIEW_INVITE,
  GET_ORGANIZATION_INTERVIEW_INVITES,
} from "../gql";

interface DeleteConnectionVariables {
  connectionCode: string;
}

interface DeleteConnectionResponse {
  deleteConnection: DeleteConnectionPayload;
}

export interface UseDeleteConnectionResult {
  deleteConnection: (connectionCode: string) => Promise<Connection | null>;
  loading: boolean;
  error: Error | null;
}

export function useDeleteConnection(): UseDeleteConnectionResult {
  const [deleteInviteMutation, { loading, error }] = useMutation<
    DeleteConnectionResponse,
    DeleteConnectionVariables
  >(DELETE_INTERVIEW_INVITE, {
    errorPolicy: "all",
    onError: (error) => {
      console.error("Interview invite deletion error:", error);
    },
    // Refetch invites after deletion
    refetchQueries: [GET_ORGANIZATION_INTERVIEW_INVITES],
  });

  const deleteConnection = async (
    connectionCode: string,
  ): Promise<Connection | null> => {
    try {
      const result = await deleteInviteMutation({
        variables: { connectionCode },
      });

      const deletedInvite = result.data?.deleteConnection?.connection;
      return deletedInvite || null;
    } catch (err) {
      console.error("Error deleting interview invite:", err);
      throw err;
    }
  };

  return {
    deleteConnection,
    loading,
    error: error || null,
  };
}
